// SEO Utilities for Park Plus
import { defaultSEO, pageSEO, structuredDataTemplates, airportSEO } from '../config/seoConfig';

/**
 * Generate page-specific SEO data
 * @param {string} pageKey - Key for page-specific SEO data
 * @param {object} overrides - Any custom overrides for the page
 * @returns {object} SEO data object
 */
export const generatePageSEO = (pageKey, overrides = {}) => {
  const baseSEO = pageSEO[pageKey] || {};
  
  return {
    title: overrides.title || baseSEO.title || defaultSEO.title,
    description: overrides.description || baseSEO.description || defaultSEO.description,
    keywords: overrides.keywords || baseSEO.keywords || defaultSEO.keywords,
    url: overrides.url || `${defaultSEO.siteUrl}${baseSEO.path || ''}`,
    image: overrides.image || `${defaultSEO.siteUrl}${defaultSEO.defaultImage}`,
    ...overrides
  };
};

/**
 * Generate airport-specific SEO data
 * @param {string} airportCode - Airport code (heathrow, gatwick, etc.)
 * @param {object} overrides - Any custom overrides
 * @returns {object} SEO data object
 */
export const generateAirportSEO = (airportCode, overrides = {}) => {
  const airportData = airportSEO[airportCode.toLowerCase()] || {};
  
  return {
    title: overrides.title || airportData.title || defaultSEO.title,
    description: overrides.description || airportData.description || defaultSEO.description,
    keywords: overrides.keywords || airportData.keywords || defaultSEO.keywords,
    url: overrides.url || `${defaultSEO.siteUrl}/search?airport=${airportCode}`,
    image: overrides.image || `${defaultSEO.siteUrl}${defaultSEO.defaultImage}`,
    ...overrides
  };
};

/**
 * Generate structured data for different content types
 * @param {string} type - Type of structured data (organization, service, breadcrumb, etc.)
 * @param {object} data - Custom data to merge with template
 * @returns {object} Structured data object
 */
export const generateStructuredData = (type, data = {}) => {
  const template = structuredDataTemplates[type] || {};
  
  switch (type) {
    case 'organization':
      return {
        ...template,
        ...data
      };
      
    case 'service':
      return {
        ...template,
        ...data
      };
      
    case 'website':
      return {
        ...template,
        ...data
      };
      
    case 'breadcrumb':
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": data.items || []
      };
      
    case 'faq':
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.questions || []
      };
      
    case 'offer':
      return {
        "@context": "https://schema.org",
        "@type": "Offer",
        "name": data.name || "Airport Parking Deal",
        "description": data.description || "Save on airport parking",
        "price": data.price,
        "priceCurrency": data.currency || "GBP",
        "availability": "https://schema.org/InStock",
        "validThrough": data.validThrough,
        "seller": {
          "@type": "Organization",
          "name": "Park Plus"
        }
      };
      
    default:
      return { ...template, ...data };
  }
};

/**
 * Generate breadcrumb structured data
 * @param {array} breadcrumbs - Array of breadcrumb items
 * @returns {object} Breadcrumb structured data
 */
export const generateBreadcrumbStructuredData = (breadcrumbs) => {
  const items = breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": crumb.url
  }));
  
  return generateStructuredData('breadcrumb', { items });
};

/**
 * Generate FAQ structured data
 * @param {array} faqs - Array of FAQ objects with question and answer
 * @returns {object} FAQ structured data
 */
export const generateFAQStructuredData = (faqs) => {
  const questions = faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }));
  
  return generateStructuredData('faq', { questions });
};

/**
 * Generate Open Graph meta tags
 * @param {object} seoData - SEO data object
 * @returns {array} Array of Open Graph meta tags
 */
export const generateOpenGraphTags = (seoData) => {
  return [
    { property: "og:title", content: seoData.title },
    { property: "og:description", content: seoData.description },
    { property: "og:image", content: seoData.image },
    { property: "og:url", content: seoData.url },
    { property: "og:type", content: seoData.type || "website" },
    { property: "og:site_name", content: defaultSEO.siteName },
    { property: "og:locale", content: defaultSEO.locale }
  ];
};

/**
 * Generate Twitter Card meta tags
 * @param {object} seoData - SEO data object
 * @returns {array} Array of Twitter Card meta tags
 */
export const generateTwitterCardTags = (seoData) => {
  return [
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: defaultSEO.twitterHandle },
    { name: "twitter:creator", content: defaultSEO.twitterHandle },
    { name: "twitter:title", content: seoData.title },
    { name: "twitter:description", content: seoData.description },
    { name: "twitter:image", content: seoData.image }
  ];
};

/**
 * Clean and optimize meta description
 * @param {string} description - Raw description text
 * @param {number} maxLength - Maximum length (default 160)
 * @returns {string} Optimized description
 */
export const optimizeMetaDescription = (description, maxLength = 160) => {
  if (!description) return defaultSEO.description;
  
  // Clean HTML tags and extra spaces
  const cleaned = description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  
  if (cleaned.length <= maxLength) return cleaned;
  
  // Truncate at word boundary
  const truncated = cleaned.substr(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return truncated.substr(0, lastSpace) + '...';
};

/**
 * Generate canonical URL
 * @param {string} path - Page path
 * @param {object} params - URL parameters to exclude
 * @returns {string} Canonical URL
 */
export const generateCanonicalURL = (path, excludeParams = []) => {
  const url = new URL(path, defaultSEO.siteUrl);
  
  // Remove specified parameters for canonical URL
  excludeParams.forEach(param => {
    url.searchParams.delete(param);
  });
  
  return url.toString();
};

/**
 * Validate SEO data completeness
 * @param {object} seoData - SEO data to validate
 * @returns {object} Validation results
 */
export const validateSEOData = (seoData) => {
  const warnings = [];
  const errors = [];
  
  if (!seoData.title) {
    errors.push('Title is required');
  } else if (seoData.title.length > 60) {
    warnings.push('Title is longer than 60 characters');
  }
  
  if (!seoData.description) {
    errors.push('Description is required');
  } else if (seoData.description.length > 160) {
    warnings.push('Description is longer than 160 characters');
  } else if (seoData.description.length < 120) {
    warnings.push('Description is shorter than 120 characters');
  }
  
  if (!seoData.keywords) {
    warnings.push('Keywords are missing');
  }
  
  if (!seoData.image) {
    warnings.push('Open Graph image is missing');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

